import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const DEFAULT_TITLE = "Dreamtime Resorts | Time Tracker";

function formatTimer(seconds) {
  if (!seconds || seconds < 0) seconds = 0;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
}

/**
 * Mounted once for authenticated users. Polls the backend periodically for a
 * running entry and updates document.title every second with a live elapsed
 * counter — like Toggl's tab title. Resets to the default when no timer is
 * running.
 */
export default function RunningTitleManager() {
  const [running, setRunning] = useState(null);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  // Poll the backend every 20s for the running entry. Cheap & avoids needing
  // a shared global store between pages.
  useEffect(() => {
    let cancelled = false;
    const fetchRunning = async () => {
      try {
        const { data } = await api.get("/time-entries", { params: { scope: "self" } });
        if (cancelled) return;
        const r = (data || []).find((e) => !e.end);
        setRunning(r || null);
      } catch {
        /* ignore — user might be logged out */
      }
    };
    fetchRunning();
    pollRef.current = setInterval(fetchRunning, 20000);
    // Listen for cross-component announcements (start/stop/patch in TimerBar)
    const onChange = () => fetchRunning();
    window.addEventListener("dtr-running-changed", onChange);
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
      window.removeEventListener("dtr-running-changed", onChange);
    };
  }, []);

  // Update document.title every second when running; reset on stop.
  useEffect(() => {
    clearInterval(tickRef.current);
    const setFavicon = (href) => {
      const link = document.getElementById("favicon");
      if (link && link.getAttribute("href") !== href) link.setAttribute("href", href);
    };
    if (!running) {
      document.title = DEFAULT_TITLE;
      setFavicon("/favicon-idle.svg");
      return;
    }
    setFavicon("/favicon-running.svg");
    const startMs = new Date(running.start).getTime();
    const apply = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
      const desc = (running.description || "").trim();
      const counter = formatTimer(elapsed);
      document.title = desc
        ? `${counter} · ${desc} — ${DEFAULT_TITLE}`
        : `${counter} — ${DEFAULT_TITLE}`;
    };
    apply();
    tickRef.current = setInterval(apply, 1000);
    return () => clearInterval(tickRef.current);
  }, [running]);

  // Also reset on unmount (e.g., logout)
  useEffect(() => () => {
    document.title = DEFAULT_TITLE;
    const link = document.getElementById("favicon");
    if (link) link.setAttribute("href", "/favicon-idle.svg");
  }, []);

  return null;
}
